<script>
    import ListItemSeparator from "./ListItemSeparator.svelte";

    import * as manifest from "../extension/manifest.json";
    import ListItem from "./ListItem.svelte";

    import { PreferencesStore } from "$/stores/global/PreferencesStore";
    import { TokenStore } from "$/stores/TokenStore";
    import PanelHeader from "./panels/_components/PanelHeader.svelte";
    import { createEventDispatcher, onMount } from "svelte";
    import { ServiceConsts } from "$/services/ServiceConsts";
    import { redirect } from "@roxi/routify";
    import PricingPlanItem from "./PricingPlanItem.svelte";
    import { BackgroundService } from "$/services/BackgroundService";
    import { UserService } from "$/services/UserService";
    import { TenantStore } from "$/stores/TenantStore";
    import { ActiveTopicStore } from "$/stores/extension/ActiveTopicStore";
    import { AppStore, Panels } from "$/stores/AppStore";
    import { ClusterStore } from "$/stores/ClusterStore";
    import SlackWorkspaceItem from "./SlackWorkspaceItem.svelte";
    import slackIcon from "$/assets/images/slack-icon.svg";
    import { ExtensionStore } from "$/stores/ExtensionStore";
    import { highlightSnippets } from "$/utils/CustomLocateV4/marker/utils";

    export let standalone;

    let UserServiceImpl = chrome?.storage ? BackgroundService : UserService;

    let slackWorkspaces = [];

    const dispatch = createEventDispatcher();

    async function logout() {
        // extension doesn't use routify
        if (!standalone) {
            // if extension, redirect to new window for logout, then close it
            const logoutWindow = window.open(
                ServiceConsts.LOGOUT_URL,
                "_blank",
            );

            await TokenStore.logout();
            await TenantStore.logout();
            ClusterStore.clear();

            logoutWindow.close();
        } else {
            $redirect("/auth/logout");
        }

        dispatch("close");
    }

    onMount(async () => {
        // TODO: Services should never be invoked from the UI, this should be moved to a store.
        if ($TenantStore !== null) {
            slackWorkspaces = await UserServiceImpl.getSlackWorkspaces();
        }
    });

    const personalTenantIcon = `<path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />`;
    const teamTenantIcon = `<path stroke-linecap="round" stroke-linejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />`;

    async function switchTenant(tenant) {
        if (tenant.id === $TenantStore?.active.id) {
            return;
        }

        ExtensionStore.setLoadingStatus(true);

        dispatch("close");

        ActiveTopicStore.clear();
        await TenantStore.setActive(tenant);
        await ClusterStore.init(tenant.id); // HACK: TenantStore is null at this point although at the end of setActive is not

        let topics = ClusterStore.getAllClusterTopics();
        const topicToLoad =
            ClusterStore.getFirstNonArchivedTopic() ?? topics?.[0] ?? null;

        await ActiveTopicStore.init(
            topicToLoad?.id,
            TokenStore,
            TenantStore,
            ClusterStore,
        );

        highlightSnippets();

        ExtensionStore.setLoadingStatus(false);
    }

    $: hasCopilotBetaAccess = $TenantStore?.activeAuthorities?.find(
        (a) => a.authority === "ROLE_COPILOT_BETA",
    );

    $: defaultTopic = $PreferencesStore.openInboxOnStartup ? "inbox" :
                      $PreferencesStore.openByUrlOnStartup ? "url" :
                      "default";
    function onDefaultTopicChange(value) {
        $PreferencesStore.openInboxOnStartup = value === "inbox";
        $PreferencesStore.openByUrlOnStartup = value === "url";
    }
</script>

<div class="settings-panel">
    <PanelHeader title="Settings" on:close={() => dispatch("close")} />

    <div class="settings-panel__items">
        {#if standalone}
            <ListItemSeparator>Account</ListItemSeparator>

            <ListItem
                onClick={() => AppStore.setPanel(Panels.USER_SETTINGS)}
                title={"User settings"}
                icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />`}
            ></ListItem>

            <ListItemSeparator>Connected Slack Workspace</ListItemSeparator>

            {#each slackWorkspaces as slackWorkspace (slackWorkspace.id)}
                <SlackWorkspaceItem workspace={slackWorkspace} />
            {/each}

            <ListItem
                url={ServiceConsts.SLACK_DISTRIBUTION_APP_URL +
                    $TokenStore?.user?.id}
                title={"Add new slack workspace"}
                openInNewTab={false}
                animateIcon={false}
                linkIcon={`<path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />`}
                image={slackIcon}
            ></ListItem>
        {/if}

        <ListItemSeparator>Useful links</ListItemSeparator>

        {#if !standalone}
            <ListItem
                url={"http://app.collabwriting.com"}
                title={"Dashboard"}
                icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />`}
            ></ListItem>
        {/if}

        <ListItem
            url={"http://collabwriting.com"}
            title={"Visit website"}
            icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />`}
        ></ListItem>

        <ListItem
            url={"https://collabwriting.com/roadmap"}
            title={"Roadmap"}
            icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />`}
        ></ListItem>

        <ListItem
            url={"https://collabwriting.sleekplan.app/satisfaction"}
            title={"Send us feedback"}
            icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />`}
        ></ListItem>

        <ListItem
            url={"https://chrome.google.com/webstore/detail/collabwriting/jjefmmjicedljonbpdoeceklnhobmolo"}
            title={"Leave a review"}
            icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />`}
        ></ListItem>

        <ListItem
            url={ServiceConsts.HELP_URL}
            title={"Help"}
            icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />`}
        ></ListItem>

        {#if !standalone}
            <ListItem
                url={"https://app.collabwriting.com/welcome/"}
                title={"Getting started with Collabwriting"}
                icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />`}
            ></ListItem>
            {#if hasCopilotBetaAccess}
                <ListItem
                    url={"https://app.collabwriting.com/copilot/"}
                    title={"Copilot"}
                    icon={`<path d="M12.1837 3.00035C9.21514 3.02533 7.1962 4.44946 5.71379 7.07656C5.52922 7.40364 5.29053 7.94121 5.29053 7.94121V15.6613C5.29053 15.6613 6.45803 17.9107 7.70918 18.8111C8.29626 19.2336 8.64503 19.4978 9.34177 19.6757C11.0348 20.1081 13.4535 20.1081 15.2675 19.6757C16.0707 19.4843 16.5358 19.3072 17.2024 18.8111C18.3514 17.9559 19.1373 15.6613 19.1373 15.6613V7.94121C19.1373 7.94121 18.9368 7.40439 18.7745 7.07656C17.4165 4.334 15.1925 2.97502 12.1837 3.00035Z" stroke-linejoin="round"/>
					<path d="M22.1609 8.55253C21.4839 7.75246 19.1367 7.38721 19.1367 7.38721V16.2985C19.1367 16.2985 21.3869 15.2958 22.1609 14.3106C22.3436 14.078 22.5641 13.6937 22.5641 13.6937V9.30656C22.5641 9.30656 22.3907 8.82411 22.1609 8.55253Z" stroke-linejoin="round"/>
					<path d="M2.3873 8.55253C3.03724 7.75246 5.29053 7.38721 5.29053 7.38721V16.2985C5.29053 16.2985 3.13037 15.2958 2.3873 14.3106C2.21191 14.078 2.00021 13.6937 2.00021 13.6937V9.30656C2.00021 9.30656 2.16668 8.82411 2.3873 8.55253Z" stroke-linejoin="round"/>
					<path d="M5.29053 9.99426C5.36794 10.622 6.61601 10.9774 7.23754 11.1544C7.25933 11.1606 7.28035 11.1666 7.30052 11.1723C8.86857 11.6208 10.2985 11.6427 12.2884 11.6364C14.2218 11.6303 15.7546 11.5628 17.1273 11.1701C17.1475 11.1644 17.1685 11.1584 17.1903 11.1522C17.8118 10.9752 19.0599 10.6198 19.1373 9.99205"/>
					<path d="M5.29053 14.1071C5.36794 14.7348 6.61601 15.0902 7.23754 15.2672C7.25933 15.2734 7.28035 15.2794 7.30052 15.2851C8.86857 15.7336 10.2985 15.7555 12.2884 15.7492C14.2218 15.7431 15.7546 15.6756 17.1273 15.2829C17.1475 15.2772 17.1685 15.2712 17.1903 15.265C17.8118 15.088 19.0599 14.7326 19.1373 14.1048"/>`}
                ></ListItem>
            {/if}
        {/if}

        <ListItem
            url={"https://read.amazon.com/notebook"}
            title={"Kindle Book Notes"}
            icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5h3m-6.75 2.25h10.5a2.25 2.25 0 002.25-2.25v-15a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 4.5v15a2.25 2.25 0 002.25 2.25z" />`}
        ></ListItem>

        <ListItemSeparator>Legal</ListItemSeparator>

        <ListItem
            url={"http://collabwriting.com/legal/terms-of-service"}
            title={"Terms of Service"}
            icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />`}
        ></ListItem>

        <ListItem
            url={"http://collabwriting.com/legal/privacy-policy"}
            title={"Privacy Policy"}
            icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />`}
        ></ListItem>

        <ListItem
            url={"http://collabwriting.com/legal/refund-policy"}
            title={"Refund Policy"}
            icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />`}
        ></ListItem>

        {#if !standalone}
            <ListItemSeparator>Open on Startup</ListItemSeparator>
            <ListItem
                title={"Inbox"}
                icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M9 3.75H6.912a2.25 2.25 0 0 0-2.15 1.588L2.35 13.177a2.25 2.25 0 0 0-.1.661V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 0 0-2.15-1.588H15M2.25 13.5h3.86a2.25 2.25 0 0 1 2.012 1.244l.256.512a2.25 2.25 0 0 0 2.013 1.244h3.218a2.25 2.25 0 0 0 2.013-1.244l.256-.512a2.25 2.25 0 0 1 2.013-1.244h3.859M12 3v8.25m0 0-3-3m3 3 3-3" />`}
                onClick={() => onDefaultTopicChange("inbox")}
                color={defaultTopic === "inbox" ? "blue": null}
            ></ListItem>
            <ListItem
                title={"My Last Viewed Topic"}
                icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z" />`}
                onClick={() => onDefaultTopicChange("default")}
                color={defaultTopic === "default" ? "blue": null}
            ></ListItem>
            <ListItem
                title={"Newest Topic on this Link"}
                icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />`}
                onClick={() => onDefaultTopicChange("url")}
                color={defaultTopic === "url" ? "blue": null}
                isBeta={true}
            ></ListItem>

            <ListItemSeparator>Preferences</ListItemSeparator>
            <ListItem
                title={"Open panel on the right"}
                icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />`}
                bind:value={$PreferencesStore.positionPanelRight}
            ></ListItem>

            <ListItem
                title={"Keep open on every page"}
                icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />`}
                bind:value={$PreferencesStore.openOnEveryPage}
            ></ListItem>
            <ListItem
                title={"Hide highlighter button"}
                icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />`}
                bind:value={$PreferencesStore.hideHighlighterButton}
            ></ListItem>
            <ListItem
                title={"Add cards to the bottom"}
                icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M7.5 7.5h-.75A2.25 2.25 0 0 0 4.5 9.75v7.5a2.25 2.25 0 0 0 2.25 2.25h7.5a2.25 2.25 0 0 0 2.25-2.25v-7.5a2.25 2.25 0 0 0-2.25-2.25h-.75m-6 3.75 3 3m0 0 3-3m-3 3V1.5m6 9h.75a2.25 2.25 0 0 1 2.25 2.25v7.5a2.25 2.25 0 0 1-2.25 2.25h-7.5a2.25 2.25 0 0 1-2.25-2.25v-.75" />`}
                bind:value={$PreferencesStore.addCardsToTheBottom}
            ></ListItem>
            <ListItem
                title={"Focus Bookmark on Startup"}
                icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M16.5 3.75V16.5L12 14.25 7.5 16.5V3.75m9 0H18A2.25 2.25 0 0 1 20.25 6v12A2.25 2.25 0 0 1 18 20.25H6A2.25 2.25 0 0 1 3.75 18V6A2.25 2.25 0 0 1 6 3.75h1.5m9 0h-9" />`}
                bind:value={$PreferencesStore.focusBookmarkOnStartup}
            ></ListItem>
            <ListItem
                title={"Show All Highlights"}
                icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v16.5c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Zm3.75 11.625a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />`}
                bind:value={$PreferencesStore.showAllHighlights}
            ></ListItem>
            <ListItem
                title={"Experimental Features"}
                icon={`<path d="M7.2 1.1h.2c2.2-.2 4.4-.2 6.5 0h.2M17 11.5h0c.8 1 1.4 2 1.9 3v.1" class="st0"/><path d="M17.3 12.2 14 14.1l-.8-1.2V1.3" class="st1"/><path d="M8 1.3v11.5l-3.3 5.4c-.3.5.1 1.2.7 1.2h10.4c.6 0 1-.7.7-1.2l-1.2-1.9 3.3-1.9" class="st1"/>`}
                bind:value={$PreferencesStore.betaFeatures}
            ></ListItem>
            <ListItem
                title={"AI Topic & Hashtag Suggestions"}
                icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M4.098 19.902a3.75 3.75 0 0 0 5.304 0l6.401-6.402M6.75 21A3.75 3.75 0 0 1 3 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 0 0 3.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008Z" />`}
                isDisabled={true}
            ></ListItem>
            <ListItem
                title={"Resize Page"}
                icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15M12 9l-3 3m0 0 3 3m-3-3h12.75" />`}
                isDisabled={true}
            ></ListItem>
            <ListItem
                title={"Most Highlighted"}
                icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 0 1 0-3.75Z" />`}
                isDisabled={true}
            ></ListItem>
        {/if}


        {#if $TokenStore?.user}
            <ListItemSeparator>Plan</ListItemSeparator>

            <PricingPlanItem {standalone} />
        {/if}

        {#if !standalone && $TenantStore?.tenants?.length > 1}
            <ListItemSeparator>Workspaces</ListItemSeparator>

            {#each $TenantStore?.tenants as tenant}
                <ListItem
                    onClick={() => switchTenant(tenant)}
                    id={tenant.id}
                    title={tenant.name}
                    icon={tenant.personal ? personalTenantIcon : teamTenantIcon}
                    color={$TenantStore?.active.id === tenant.id
                        ? "blue"
                        : null}
                ></ListItem>
            {/each}
        {/if}

        {#if $TokenStore?.user}
            <ListItemSeparator>Actions</ListItemSeparator>

            <ListItem
                onClick={logout}
                title={"Log out"}
                icon={`<path stroke-linecap="round" stroke-linejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />`}
            ></ListItem>
        {/if}
    </div>

    <footer>
        <span class="made-by"> Made with ♥ by the Collabwriting Team </span>

        <span class="version">
            {manifest.version}
            {#if ServiceConsts.BUILD_MODE === "DEV"}
                {ServiceConsts.BUILD_MODE}
            {/if}
        </span>
    </footer>
</div>

<style>
    @import "../main.css";

    .settings-panel {
        @apply reset flex h-full w-full flex-col bg-gray-100 !important;
    }

    .settings-panel__items {
        @apply flex-1 overflow-auto pb-4 !important;
    }

    footer {
        @apply reset mt-auto flex w-full flex-row justify-between p-4 shadow-[0_-10px_10px_4px_rgba(243,244,245,1)] !important;
    }

    .made-by {
        @apply reset text-xs text-gray-500 !important;
    }

    .version {
        @apply reset text-xs text-gray-500 !important;
    }
</style>
